import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Bubble, Sender, ThoughtChain } from '@ant-design/x';
import { Avatar, Button, ConfigProvider, Spin, Tooltip, message as antdMessage } from 'antd';
import { Bot, Info } from 'lucide-react';
import { Cookies } from 'react-cookie';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { sendBotChat, loadConversation } from './botApi';

const cookies = new Cookies();

function getUserInitials() {
    try {
        const user = cookies.get('user');
        const name = user && user.name ? String(user.name).trim() : '';
        if (!name) return '?';
        return name.slice(0, 2);
    } catch (e) {
        return '?';
    }
}

const WELCOME = {
    role: 'assistant',
    content: '你好！我可以帮你查询：\n- **订单**（含发货/到货/货期）\n- **开出账单**（我们发给客户的账单 / Invoice）\n- **收到账单**（供应商发给我们的账单 / In-Invoice）\n- **合同**（含付款/欠款/到期）\n- **产品**（按型号、品牌、订单/账单/合同找产品）\n- **库存**（按型号/位置查库存，出入库记录）\n\n试试问我：\n- "有哪些逾期账单"\n- "ZCKE05 在哪些订单里"\n- "Schmersal 现货还剩多少"',
    isWelcome: true,
};

const renderAssistantContent = (content) => (
    <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
);

export default function BotConversation({ conversationId, onConversationChange }) {
    // 新对话起始就一条欢迎语；加载已有会话时会被替换
    const [messages, setMessages] = useState([WELCOME]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [expandedTrace, setExpandedTrace] = useState({});
    const listRef = useRef(null);
    const userInitials = useMemo(getUserInitials, []);

    // 切换到已有会话时拉历史；切换到新对话（id=null）时重置欢迎语
    useEffect(() => {
        if (conversationId == null) {
            setMessages([WELCOME]);
            setExpandedTrace({});
            return;
        }
        let cancelled = false;
        setHistoryLoading(true);
        loadConversation(conversationId)
            .then((data) => {
                if (cancelled) return;
                const msgs = (data.messages || []).map((m) => ({
                    role: m.role,
                    content: m.content || '',
                    toolTrace: m.tool_trace || undefined,
                }));
                setMessages(msgs.length ? msgs : [WELCOME]);
                setExpandedTrace({});
            })
            .catch(() => {
                if (cancelled) return;
                antdMessage.error('加载会话失败');
            })
            .finally(() => { if (!cancelled) setHistoryLoading(false); });
        return () => { cancelled = true; };
    }, [conversationId]);

    useEffect(() => {
        if (listRef.current) {
            listRef.current.scrollTop = listRef.current.scrollHeight;
        }
    }, [messages, loading, historyLoading]);

    const send = async (text) => {
        const t = (text || '').trim();
        if (!t || loading || historyLoading) return;
        const userMsg = { role: 'user', content: t };
        const next = [...messages, userMsg];
        setMessages(next);
        setInput('');
        setLoading(true);
        try {
            // 发给后端的 messages 不含前端欢迎语
            const payload = next.filter(m => !m.isWelcome).map(({ role, content }) => ({ role, content }));
            const data = await sendBotChat(payload, conversationId);
            setMessages([...next, {
                role: 'assistant',
                content: data.reply || '(空回复)',
                toolTrace: data.tool_trace,
            }]);
            // 通知父组件：可能是新创建的 conversation，同步 id 给 sidebar
            if (data.conversation_id && data.conversation_id !== conversationId) {
                onConversationChange?.(data.conversation_id);
            } else if (data.conversation_id) {
                // 触发 sidebar 刷新 updated_at 排序
                onConversationChange?.(data.conversation_id);
            }
        } catch (e) {
            const msg = e.response?.data?.message || e.message || '未知错误';
            setMessages([...next, { role: 'assistant', content: '⚠️ ' + msg, isError: true }]);
        } finally {
            setLoading(false);
        }
    };

    const bubbleItems = messages.map((m, idx) => {
        const isUser = m.role === 'user';
        const base = {
            key: idx,
            placement: isUser ? 'end' : 'start',
            content: isUser ? m.content : renderAssistantContent(m.content),
            avatar: isUser
                ? <Avatar style={{ backgroundColor: '#5bc0de', color: '#FFF', fontSize: 13 }}>{userInitials}</Avatar>
                : <Avatar icon={<Bot size={18} />} />,
        };
        if (!isUser && m.toolTrace && m.toolTrace.length > 0) {
            base.footer = (
                <div style={{ marginTop: 4 }}>
                    <Tooltip title="查看调用过程">
                        <Button
                            type="text"
                            size="small"
                            icon={<Info size={14} />}
                            onClick={() => setExpandedTrace(prev => ({ ...prev, [idx]: !prev[idx] }))}
                        />
                    </Tooltip>
                    {expandedTrace[idx] && (
                        <ThoughtChain
                            items={m.toolTrace.map((t, i) => ({
                                key: i,
                                title: t.tool,
                                description: t.result_summary,
                                content: <pre style={{ fontSize: 12 }}>{JSON.stringify(t.args, null, 2)}</pre>,
                            }))}
                        />
                    )}
                </div>
            );
        }
        return base;
    });

    if (loading) {
        bubbleItems.push({
            key: 'loading',
            placement: 'start',
            avatar: <Avatar icon={<Bot size={18} />} />,
            loading: true,
        });
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div ref={listRef} style={{ flex: 1, overflowY: 'auto', padding: '8px 12px' }}>
                {historyLoading ? (
                    <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>
                ) : (
                    <Bubble.List items={bubbleItems} />
                )}
            </div>
            <div className="bot-sender-wrap" style={{ padding: 8, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                <style>{`
                    .bot-sender-wrap textarea,
                    .bot-sender-wrap input {
                        caret-color: #FFF !important;
                    }
                `}</style>
                <ConfigProvider theme={{
                    components: {
                        Input: {
                            colorBgContainer: 'rgba(255,255,255,0.08)',
                            colorText: '#FFF',
                            colorTextPlaceholder: 'rgba(255,255,255,0.55)',
                            colorBorder: '#5bc0de',
                        },
                    },
                }}>
                    <Sender
                        value={input}
                        onChange={setInput}
                        onSubmit={send}
                        loading={loading}
                        disabled={historyLoading}
                        placeholder="问我订单、账单、合同、产品……"
                    />
                </ConfigProvider>
            </div>
        </div>
    );
}
